import { TransformedUseCase } from '@repo/editor/src/types';

import { ALL_SLUG } from 'global/utils/templateParams';

import { USECASE_ICONS } from './CategorySection/config';

export const TRENDING_LISTS_LIMIT = 15;
export const USE_CASE_MINIMUM_TOTAL = 5;

// useCases based on the active parent SubCategory or Category
export const getMergedUseCases = (
  allUseCases: TransformedUseCase[],
  currentCategorySlug?: string,
  currentSubCategorySlug?: string
): TransformedUseCase[] => {
  const currentCategory = allUseCases.find(
    (category) => category.slug === currentCategorySlug
  );

  return (
    // If there is an active category, get the use cases from the active category
    (currentCategory
      ? // If there is an active subcategory, get the use cases from the active subcategory
        currentSubCategorySlug !== ALL_SLUG
        ? currentCategory?.subCategories?.find(
            (subCategory) => subCategory.slug === currentSubCategorySlug
          )?.useCases
        : // If there is no active subcategory, get all use cases from all subcategories of the active category
          currentCategory?.subCategories?.flatMap(
            (subCategory) => subCategory.useCases || []
          )
      : // If there is no active category, get all use cases from all categories
        allUseCases.flatMap(
          (category) =>
            category.subCategories?.flatMap(
              (subCategory) => subCategory.useCases || []
            ) || []
        )) || []
  );
};

export type TransformedSubUseCase = TransformedUseCase & {
  parentUseCaseSlug: string;
};

// get subUseCases based on the active useCase
export const getMergedSubUseCases = (
  allUseCases: TransformedUseCase[],
  currentCategorySlug: string,
  currentSubCategorySlug: string,
  currentUseCaseSlug: string
): TransformedSubUseCase[] => {
  const mergedUseCases = getMergedUseCases(
    allUseCases,
    currentCategorySlug,
    currentSubCategorySlug
  );
  const currentUseCase = mergedUseCases.find(
    (useCases) => useCases.slug === currentUseCaseSlug
  );

  return currentUseCase
    ? currentUseCase.subUseCases?.map((subUseCase) => ({
        ...subUseCase,
        parentUseCaseSlug: currentUseCaseSlug,
      })) || []
    : mergedUseCases.flatMap((useCase) => {
        return (
          useCase.subUseCases?.map((subUseCase) => ({
            ...subUseCase,
            parentUseCaseSlug: useCase.slug,
          })) || []
        );
      });
};

export const getCurrentUseCase = (
  allUseCases: TransformedUseCase[],
  currentCategorySlug?: string,
  currentSubCategorySlug?: string,
  currentUseCaseSlug?: string,
  currentSubUseCaseSlug?: string
): TransformedUseCase | null => {
  if (currentSubUseCaseSlug && currentSubUseCaseSlug !== ALL_SLUG) {
    return (
      getMergedSubUseCases(
        allUseCases,
        currentCategorySlug!,
        currentSubCategorySlug!,
        currentUseCaseSlug!
      ).find((x) => x.slug === currentSubUseCaseSlug) ?? null
    );
  }

  if (currentUseCaseSlug !== ALL_SLUG) {
    return (
      getMergedUseCases(
        allUseCases,
        currentCategorySlug!,
        currentSubCategorySlug!
      ).find((x) => x.slug === currentUseCaseSlug) ?? null
    );
  }

  return null;
};

export const filterCategories = (
  allUseCases: TransformedUseCase[]
): TransformedUseCase[] => {
  return allUseCases.filter(
    (useCase) =>
      !useCase.hidden &&
      // Restricting categories temporarily
      // We only want to show the categories that have icons
      Object.keys(USECASE_ICONS).includes(useCase.slug)
  );
};

export const filterAndSortUseCases = (
  useCases: TransformedUseCase[]
): TransformedUseCase[] => {
  return useCases
    .filter(
      (useCase) => !useCase.hidden && useCase.total >= USE_CASE_MINIMUM_TOTAL
    )
    .sort((a, b) => b.popularity - a.popularity);
};
